// UserUserPointer.js - Fix where dependency with deep comparison
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import objectToOption from "./objectToOption";
import RelationDialog from "nq-component/dist/RelationDialog";
import { dialog } from "nq-component";
import filterFields from "nq-component/dist/filterFields";
import GetOption from "./GetOption";
import SelectSearch from "../SelectSearch";
import { saveObjectUseCase } from "../../usecases/object";
import { saveFileUseCase, saveImageUseCase } from "../../usecases/file";

const defaultProps = {
  include: [],
};

// Deep equality check for objects
function deepEqual(obj1, obj2) {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

// Custom hook to get stable reference only when value actually changes
function useDeepMemo(value) {
  const ref = useRef(value);

  if (!deepEqual(ref.current, value)) {
    ref.current = value;
  }

  return ref.current;
}

function UserPointer({
  schema,
  object,
  pick,
  target,
  onChange,
  isMulti,
  where = {},
  indexes,
  field,
  defaultValue,
  findObject,
  saveObject,
  saveFile,
  saveImage,
  schemas,
  keys,
  dynamic,
  sort,
  include,
  loadOnFocus = false,
  ...props
}) {
  const [value, setValue] = useState(isMulti ? [] : { label: "", value: "" });
  const [options, setOptions] = useState([]);
  const [focus, setFocus] = useState(false);
  const [optionsRequested, setOptionsRequested] = useState(!loadOnFocus);
  const needsRefreshRef = useRef(false);
  const cancelLoadRef = useRef(null);
  const findObjectRef = useRef(findObject);
  findObjectRef.current = findObject;

  // Stabilize where object - only changes when content actually differs
  const stableWhere = useDeepMemo(where);
  const stableKeys = useDeepMemo(keys);
  const stableInclude = useDeepMemo(include);

  // Memoize indexes
  const _indexes = useMemo(() => {
    if (!schema?.fields) return [];
    const items = indexes || filterFields(schema?.fields, "_index");
    return items.length > 0 ? items : ["name"];
  }, [schema, indexes]);

  // Memoize keys
  const _keys = useMemo(() => {
    return stableKeys || filterFields(schema.fields, "key");
  }, [schema, stableKeys]);

  // Stabilize defaultValue - only changes when content actually differs
  const stableDefaultValue = useDeepMemo(defaultValue);

  // Set default value
  useEffect(() => {
    if (!stableDefaultValue) {
      // Clear value when defaultValue becomes undefined/null
      setValue(isMulti ? [] : { label: "", value: "" });
      return;
    }

    if (isMulti) {
      setValue(
        stableDefaultValue.map((obj) => objectToOption(obj, _indexes, target)),
      );
    } else {
      setValue(objectToOption(stableDefaultValue, _indexes, target));
    }
  }, [_indexes, stableDefaultValue, isMulti, target]);

  // Options callback
  const callback = useCallback((newOptions) => {
    setOptions(newOptions);
  }, []);

  // Load options; cancel pagination on unmount / where change / report switch
  useEffect(() => {
    const find = findObjectRef.current;
    if (!optionsRequested || !target || !find) return undefined;

    cancelLoadRef.current?.();
    cancelLoadRef.current = GetOption(
      target,
      "",
      _indexes,
      find,
      stableWhere,
      callback,
      _keys,
      sort,
      stableInclude,
    );

    return () => {
      cancelLoadRef.current?.();
      cancelLoadRef.current = null;
    };
  }, [
    optionsRequested,
    target,
    stableWhere,
    callback,
    _indexes,
    _keys,
    sort,
    stableInclude,
  ]);

  // Handle add new item
  const onClickAdd = useCallback(() => {
    const onClickItem = (object) => {
      _onChange(objectToOption(object, _indexes, target));
      needsRefreshRef.current = true;
      dialog.close();
    };

    if (!findObject?.execute) {
      console.error("findObject is not available for RelationDialog");
      return;
    }

    const saveObjectInstance = saveObject || saveObjectUseCase();
    const saveFileInstance = saveFile || saveFileUseCase();
    const saveImageInstance = saveImage || saveImageUseCase();

    dialog.fire({
      html: (
        <RelationDialog
          onClickItem={onClickItem}
          schema={schema}
          objects={object}
          onCancel={() => dialog.close()}
          findObject={findObject}
          saveObject={saveObjectInstance}
          saveFile={saveFileInstance}
          saveImage={saveImageInstance}
          schemas={schemas}
        />
      ),
      footer: false,
    });
  }, [
    _indexes,
    target,
    findObject,
    saveObject,
    saveFile,
    saveImage,
    schema,
    schemas,
    object,
  ]);

  // Handle change
  const _onChange = useCallback(
    (option) => {
      if (isMulti) {
        setValue((prev) => {
          const newValue = [...prev, option];
          onChange(newValue.map((o) => o.object));
          return newValue;
        });
      } else {
        setValue(option);
        onChange(option.object);
      }
    },
    [isMulti, onChange],
  );

  // Handle clear
  const onClickClear = useCallback(() => {
    setValue(isMulti ? [] : { label: "", value: "" });
    onChange(isMulti ? [] : {});
  }, [isMulti, onChange]);

  // Get display value
  const displayValue = useMemo(() => {
    if (isMulti && !focus && value.length > 0) {
      const values = value.map((v) => v.label).reverse();
      return { label: values.join(", ") };
    }
    return value;
  }, [isMulti, focus, value]);

  // Filter out selected options
  const filteredOptions = useMemo(() => {
    if (isMulti) {
      const selectedIds = new Set(value.map((v) => v.value));
      return options.filter((o) => !selectedIds.has(o.value));
    }
    return options.filter((o) => o.value !== value.value);
  }, [options, value, isMulti]);

  // Handle focus with refresh / lazy first load
  const handleFocus = useCallback(
    (focused) => {
      setFocus(focused);

      if (focused) {
        if (loadOnFocus) {
          setOptionsRequested(true);
        }
        if (needsRefreshRef.current && target && findObjectRef.current) {
          cancelLoadRef.current?.();
          cancelLoadRef.current = GetOption(
            target,
            "",
            _indexes,
            findObjectRef.current,
            stableWhere,
            callback,
            _keys,
            sort,
            stableInclude,
          );
          needsRefreshRef.current = false;
        }
      }
    },
    [
      loadOnFocus,
      target,
      _indexes,
      stableWhere,
      _keys,
      sort,
      stableInclude,
      callback,
    ],
  );

  return (
    <>
      <SelectSearch
        label={`Select ${target}`}
        value={displayValue}
        onChange={_onChange}
        options={filteredOptions}
        onFocus={handleFocus}
        onClickAdd={onClickAdd}
        onClickClear={onClickClear}
        dynamic={dynamic}
        focus
        {...props}
      />
      {target === "company_lists" &&
        displayValue?.object?.relation?.length > 0 && (
          <div className="fs-sm mt-1">
            {displayValue.object.relation.map((company, index) => (
              <span key={company.id || index}>
                {company.name}
                {index < displayValue.object.relation.length - 1 && (
                  <span className="mx-2">→</span>
                )}
              </span>
            ))}
          </div>
        )}
    </>
  );
}

UserPointer.defaultProps = defaultProps;
export default UserPointer;
